/**
 * Pure presentation: takes a `CommentaryTable` and renders it as a styled
 * HTML table that resembles the source databook.
 *
 * Sheet-agnostic. The extractor on the server produces a flat row list with
 * a `kind` field per row; this renderer maps `kind` to CSS module classes
 * and lays the cells out. Reusable across IS - Summary, BS - Stratified,
 * and any future sheet we add.
 *
 * Selection: each selectable row (subtotal, total, kpi, data) gets a
 * checkbox in a trailing column. The parent owns the selection state and
 * passes (selectedKeys, onToggle); this component just renders.
 */

import React from "react";
import type { CommentaryRow, CommentaryTable } from "../commentary-types";
import styles from "./DatabookTableView.module.css";

type Props = {
  table: CommentaryTable;
  selectedKeys: Set<string>;
  onToggle: (rowKey: string) => void;
};

const DatabookTableView: React.FC<Props> = ({ table, selectedKeys, onToggle }) => {
  // Find the last KPI row so CSS can close the gold box with a bottom border.
  const lastKpiIdx = findLastIdxByKind(table.rows, "kpi");

  return (
    <div className={styles.tableWrap}>
      <table className={styles.table}>
        <tbody>
          {table.rows.map((row, i) => (
            <TableRow
              key={row.rowKey || i}
              row={row}
              isLastKpi={i === lastKpiIdx}
              columnCount={table.columnCount}
              isSelected={selectedKeys.has(row.rowKey)}
              onToggle={onToggle}
            />
          ))}
        </tbody>
      </table>
    </div>
  );
};

/* ─────────────────────────── row ─────────────────────────── */

const TableRow: React.FC<{
  row: CommentaryRow;
  isLastKpi: boolean;
  columnCount: number;
  isSelected: boolean;
  onToggle: (rowKey: string) => void;
}> = ({ row, isLastKpi, columnCount, isSelected, onToggle }) => {
  const selectable = isSelectable(row.kind);
  const rowClass = rowClasses(row, isLastKpi, isSelected && selectable);

  // Title and kpi-header rows span all data columns with a single colspan
  // cell so the band reads cleanly across the table. Indicator + checkbox
  // cells sit to the right, always present so column alignment stays
  // consistent.
  if (row.kind === "title" || row.kind === "kpi-header") {
    const text = row.cells[0]?.text || "";
    return (
      <tr className={rowClass}>
        <td colSpan={columnCount} className={styles.left}>
          {text}
        </td>
        <td className={styles.indicatorCell}></td>
        <td className={styles.checkboxCell}></td>
      </tr>
    );
  }

  return (
    <tr className={rowClass}>
      {row.cells.map((cell, i) => (
        <td
          key={i}
          className={cellClasses(cell.align, i === 0 ? row.indent : null)}
        >
          {cell.text}
        </td>
      ))}
      <td className={styles.indicatorCell}>
        {row.hasTrendingQuestion && (
          <span
            className={styles.trendingBadge}
            title={`${row.trendingQuestionCount} trending question${row.trendingQuestionCount === 1 ? "" : "s"} on this row`}
          >
            ❓ {row.trendingQuestionCount}
          </span>
        )}
      </td>
      <td className={styles.checkboxCell}>
        {selectable && (
          <input
            type="checkbox"
            checked={isSelected}
            onChange={() => onToggle(row.rowKey)}
            aria-label={`Include ${row.cells[0]?.text || row.rowKey} in commentary`}
          />
        )}
      </td>
    </tr>
  );
};

/* ─────────────────────────── class mapping ─────────────────────────── */

function rowClasses(row: CommentaryRow, isLastKpi: boolean, isSelected: boolean): string {
  const cls: string[] = [];

  switch (row.kind) {
    case "title":
      cls.push(styles.title);
      break;
    case "header":
      cls.push(styles.header);
      break;
    case "spacer":
      cls.push(styles.spacer);
      break;
    case "kpi-header":
      cls.push(styles.kpiHeader);
      break;
    case "kpi":
      cls.push(styles.kpi);
      if (isLastKpi) cls.push(styles.kpiLast);
      break;
  }

  // isGrand wins over isMajor — grey fill + medium bottom border.
  if (row.isGrand) cls.push(styles.grandTotal);
  else if (row.isMajor) cls.push(styles.subtotalMajor);

  if (row.inHiddenGroup) cls.push(styles.inHiddenGroup);
  if (isSelected) cls.push(styles.selectedRow);

  return cls.filter(Boolean).join(" ");
}

function cellClasses(align: CommentaryRow["cells"][number]["align"], indent: number | null): string {
  const cls: string[] = [styles[align]];
  if (indent !== null) {
    const indentClass = (styles as Record<string, string>)[`indent${indent}`];
    if (indentClass) cls.push(indentClass);
  }
  return cls.filter(Boolean).join(" ");
}

function findLastIdxByKind(rows: CommentaryRow[], kind: CommentaryRow["kind"]): number {
  for (let i = rows.length - 1; i >= 0; i--) {
    if (rows[i].kind === kind) return i;
  }
  return -1;
}

function isSelectable(kind: CommentaryRow["kind"]): boolean {
  return kind === "subtotal" || kind === "total" || kind === "kpi" || kind === "data";
}

export default DatabookTableView;
